import React, { useCallback, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Divider } from "antd";
import { USER_ROLES } from "utils/constants";
import { approveDeclineBundleAssignment, updateBundleAssignment } from "utils/api";
import { defaultNgiErrorMethod, successMessage } from "components/Common/responses/message";
import NGIDropdownButton from "components/Common/buttons/NGIDropdownButton";
import menuIcon from "assets/images/svg/MenuVertical.svg";
import starIcon from "assets/images/svg/Star.svg";
import starEmptyIcon from "assets/images/svg/Star-empty.svg";
import disconnectedIcon from "assets/images/svg/Disconnected.svg";
import infoIcon from "assets/images/svg/InfoBlack.svg";
import logIcon from "assets/images/svg/LOG-black.svg";
import removeIcon from "assets/images/svg/remove-customers.svg";
import { BUNDLE_ASSIGNMENT_ACTIONS } from "pages/price-management/Bundles/bundleDetails/constants";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import BundleAssignmentHistoryLogModal from "pages/price-management/Bundles/bundleDetails/BundleAssignmentHistoryLogModal";
import CustomLink from "components/Common/buttons/CustomLink";
import ReviewAssignmentModal from "pages/price-management/Bundles/bundleDetails/ReviewAssignmentModal";
import AssignCustomerBundleModal from "pages/price-management/Bundles/bundleDetails/Tabs/AssignCustomerBundleModal";
import CustomButton from "components/Common/buttons/CustomButton";

const useStyles = makeStyles({
  root: {
    padding: "30px 40px",
  },
  actions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    fontSize: 16,

    "&>div": {
      fontSize: 11,
      fontWeight: 300,
      color: "#8C8C8C",
    },
  },

  depotList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    marginTop: 15,
  },
  dataItem: {
    display: "flex",
    flexDirection: "row",
    border: "1px solid #0000001A",
    borderRadius: 5,
  },
  infoContainer: {
    display: "flex",
    padding: 20,
    flex: 2,
  },
  name: {
    fontWeight: 600,
    flex: 1,

    "&>img": {
      marginLeft: 5,
      marginTop: -2,
    },
  },
  dataContainer: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    borderTop: "none",
    borderBottom: "none",
    fontWeight: 500,
    marginRight: 10,
  },
  assignedToName: {
    fontWeight: 600,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    textDecoration: "underline",

    "&:hover": {
      cursor: "pointer",
      opacity: 0.6,
    },
  },
  category: {
    display: "flex",
    alignItems: "center",
    fontWeight: 500,
    flex: 1,
  },
  actionsContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderLeft: "1px solid #0000001A",
  },
  assignBtn: {
    fontWeight: 500,
    border: "1px dashed #0000004D",
    backgroundColor: "#f0f0f0",
    padding: "5px 20px",
    borderRadius: 3,
    textAlign: "center",
    width: "100%",

    "&:hover": {
      cursor: "pointer",
      opacity: 0.6,
    },
  },
  noDepots: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  sectionTitle: {
    color: "#777777",
    fontWeight: 400,
    fontSize: 11,
  },
  viewAll: {
    color: "#0085FF",
    border: "1px solid #0085FF",
    borderRadius: 3,
    padding: "5px 10px",
    fontSize: 11,

    "&:hover": {
      cursor: "pointer",
      opacity: 0.6,
    },
  },
  pendingActionsContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 20px",
  },
});

const CustomerDirectAssignmentsTab = ({ data, assignmentsGroups, logs, customerGroupList, update, setIsLoading }) => {
  const classes = useStyles();
  const [isOpenCreate, setIsOpenCreate] = useState(false);
  const [isOpenLogs, setIsOpenLogs] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [approveDeclineData, setApproveDeclineData] = useState(null);

  const openNew = useCallback(() => setIsOpenCreate(true), []);
  const closeNew = useCallback(() => {
    setIsOpenCreate(false);
    setActiveItem(null);
  }, []);

  const openLogs = useCallback(item => {
    setIsOpenLogs(true);
    setActiveItem(item);
  }, []);

  const closeLogs = useCallback(() => {
    setIsOpenLogs(false);
    setActiveItem(null);
  }, []);

  const [saveItem, setSaveItem] = useState(null);

  const updateData = useCallback((id, action) => {
    setIsLoading(true);
    updateBundleAssignment({ action }, id)
      .then(() => {
        successMessage(`${action} successfully`);
        update();
        setIsLoading(false);
      })
      .catch(err => {
        setIsLoading(false);
        defaultNgiErrorMethod(err);
      });
  }, []);

  const approveDecline = useCallback(item => setApproveDeclineData(item), []);
  const approveDeclineClose = useCallback(() => setApproveDeclineData(null), []);

  const onConfirmApproveDecline = useCallback((data, isApprove) => {
    setIsLoading(true);
    approveDeclineClose();
    approveDeclineBundleAssignment(data.id, isApprove)
      .then(() => {
        successMessage(`Bundle assignment was ${isApprove ? "approved" : "declined"} successfully`);
        update();
        setIsLoading(false);
      })
      .catch(err => {
        setIsLoading(false);
        defaultNgiErrorMethod(err);
      });
  }, []);

  const closeConfirmationModal = useCallback(() => setSaveItem(null), []);
  const onConfirm = useCallback(() => updateData(saveItem.id, saveItem.type), [saveItem]);

  return (
    <div className={classes.root}>
      <div className={classes.actions}>
        <div className={classes.headerContainer}>
          Customer Direct Assignment
          <div>Assign bundle to different customers. Can be assigned to multiple customers</div>
        </div>
        {(data.status === "New" || data.status === "Active") && (
          <CustomButton
            hideRoles={[USER_ROLES.CUSTOMER_CARE_LEADERSHIP, USER_ROLES.FINANCE]}
            onClick={openNew}
            size="lite"
            text="New Assignment"
            type="primary"
          />
        )}
      </div>
      <Divider />

      {Boolean(assignmentsGroups?.active?.length) && (
        <div className={classes.depotList}>
          <div className={classes.sectionTitle}>Active Assignments</div>
          {assignmentsGroups?.active?.map(item => (
            <div key={item.id} className={classes.dataItem}>
              <div className={classes.infoContainer}>
                <div className={classes.name}>
                  <>
                    {item.customerName} (
                    <CustomLink
                      color={"#0085FF"}
                      underlined
                      target="_blank"
                      to={`/customers/customer/${item.customerId}`}
                    >
                      {item.customerId}
                    </CustomLink>
                    )
                  </>

                  {item.isPrimary && <img src={starIcon} alt="is Primary" />}
                  {/*{statusFormatterWithMap(item?.status, STATUS_COLOR_MAP_ASSIGNMENTS, { marginLeft: 10 })}*/}
                </div>
              </div>
              {/*<div className={classes.category}>{textFormatter(item?.registrationCategory)}</div>*/}

              <div className={classes.dataContainer}>{`${item.activationDate} / ${item.deactivationDate || "-"}`}</div>
              <div className={classes.actionsContainer}>
                <NGIDropdownButton
                  position="left"
                  toggleEl={<img className={classes.imgBtn} src={menuIcon} alt="action-icon" />}
                  menuItems={[
                    ...(item.isPrimary
                      ? [
                          {
                            label: "Re-assign Primary Bundle",
                            subLabel: "Reassign the bundle to all customers",
                            icon: starEmptyIcon,
                            callback: () => {
                              setSaveItem({
                                id: item.id,
                                type: BUNDLE_ASSIGNMENT_ACTIONS.RE_ASSIGN,
                              });
                            },
                            roles: [
                              USER_ROLES.SYSTEM_FULL_ADMINS,
                              USER_ROLES.IT_OPERATIONS,
                              USER_ROLES.IT_OPERATIONS_LEADERSHIP,
                              USER_ROLES.CUSTOMER_CARE_LEADERSHIP,
                            ],
                          },
                          {
                            label: "Remove Primary",
                            subLabel: "Unset primary bundle from customers",
                            icon: disconnectedIcon,
                            callback: () => {
                              setSaveItem({
                                id: item.id,
                                type: BUNDLE_ASSIGNMENT_ACTIONS.REMOVE_PRIMARY,
                              });
                            },
                            roles: [
                              USER_ROLES.SYSTEM_FULL_ADMINS,
                              USER_ROLES.IT_OPERATIONS,
                              USER_ROLES.IT_OPERATIONS_LEADERSHIP,
                              USER_ROLES.CUSTOMER_CARE_LEADERSHIP,
                            ],
                          },
                        ]
                      : [
                          {
                            label: "Set as Primary Bundle",
                            subLabel: "Will overwrite the previous primary one",
                            icon: starEmptyIcon,
                            callback: () => {
                              setSaveItem({
                                id: item.id,
                                type: BUNDLE_ASSIGNMENT_ACTIONS.SET_AS_PRIMARY,
                              });
                            },
                            roles: [
                              USER_ROLES.SYSTEM_FULL_ADMINS,
                              USER_ROLES.IT_OPERATIONS,
                              USER_ROLES.IT_OPERATIONS_LEADERSHIP,
                              USER_ROLES.CUSTOMER_CARE_LEADERSHIP,
                            ],
                          },
                        ]),
                    {
                      label: "Edit Assignment Details",
                      subLabel: "View full group details and fields",
                      icon: infoIcon,
                      hideRoles: [USER_ROLES.CUSTOMER_CARE_LEADERSHIP, USER_ROLES.FINANCE],
                      callback: () => {
                        setActiveItem(item);
                        openNew();
                      },
                    },
                    {
                      label: "View Activity Log",
                      subLabel: "Timestamps for primary setup",
                      icon: logIcon,
                      callback: () => {
                        openLogs(item);
                      },
                    },
                    {
                      label: "Unassign Bundle",
                      subLabel: "Remove bundle from this group",
                      icon: removeIcon,
                      callback: () => {
                        setSaveItem({
                          id: item.id,
                          type: BUNDLE_ASSIGNMENT_ACTIONS.UN_ASSIGN,
                        });
                      },
                      roles: [
                        USER_ROLES.SYSTEM_FULL_ADMINS,
                        USER_ROLES.IT_OPERATIONS,
                        USER_ROLES.IT_OPERATIONS_LEADERSHIP,
                      ],
                    },
                  ]}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {Boolean(assignmentsGroups?.pending?.length) && (
        <div className={classes.depotList}>
          <div className={classes.sectionTitle}>Pending Assignments</div>
          {assignmentsGroups?.pending?.map(item => (
            <div key={item.id} className={classes.dataItem}>
              <div className={classes.infoContainer}>
                <div className={classes.name}>
                  <>
                    {item.customerName} (
                    <CustomLink
                      color={"#0085FF"}
                      underlined
                      target="_blank"
                      to={`/customers/customer/${item.customerId}`}
                    >
                      {item.customerId}
                    </CustomLink>
                    )
                  </>

                  {item.isPrimary && <img src={starIcon} alt="is Primary" />}
                  {/*{statusFormatterWithMap(item?.status, STATUS_COLOR_MAP_ASSIGNMENTS, { marginLeft: 10 })}*/}
                </div>
              </div>
              {/*<div className={classes.category}>{textFormatter(item?.registrationCategory)}</div>*/}

              <div className={classes.dataContainer}>{`${item.activationDate} / ${item.deactivationDate || "-"}`}</div>
              <div className={classes.pendingActionsContainer}>
                <div className={classes.viewAll} onClick={() => approveDecline(item)}>
                  VIEW ASSIGNMENT
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!assignmentsGroups?.active?.length && !assignmentsGroups?.pending?.length && (
        <div className={classes.noDepots}>No Bundle Assignments</div>
      )}

      {!!saveItem && (
        <ConfirmationModal
          description={`Are you sure you want to ${saveItem.type}?`}
          onCancel={closeConfirmationModal}
          onConfirm={onConfirm}
        />
      )}
      {isOpenLogs && <BundleAssignmentHistoryLogModal logs={logs} data={activeItem} onClose={closeLogs} />}
      {!!approveDeclineData && (
        <ReviewAssignmentModal
          onApproveDecline={onConfirmApproveDecline}
          onClose={approveDeclineClose}
          item={approveDeclineData}
        />
      )}
      {isOpenCreate && <AssignCustomerBundleModal onClose={closeNew} update={update} bundleId={data.id} />}
    </div>
  );
};

export default CustomerDirectAssignmentsTab;
